import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Checkbox,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  UploadFile,
  Delete,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import api from '../../services/api';
import { brand } from '../../theme/colors';

const MATERIAL_CATEGORIES = [
  'Cement & Concrete',
  'Bricks & Blocks',
  'Steel & Rebar',
  'Roofing Materials',
  'Timber',
  'Plumbing',
  'Electrical',
  'Paint & Finishes',
  'Tiles & Flooring',
  'Windows & Doors',
  'Hardware',
  'Other'
];

const DELIVERY_TIMEFRAMES = [
  { value: 'asap', label: 'As soon as possible (within 1 week)' },
  { value: '2_weeks', label: 'Within 2 weeks' },
  { value: '1_month', label: 'Within 1 month' },
  { value: 'flexible', label: 'Flexible - no specific deadline' },
  { value: 'specific', label: 'Specific date' }
];

const BOQUploadStep = ({ project, onNext, onBack, onCancel }) => {
  const [boqFile, setBoqFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [quoteEverything, setQuoteEverything] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [deliveryTimeframe, setDeliveryTimeframe] = useState('2_weeks');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [errors, setErrors] = useState({});

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
                       'application/vnd.ms-excel', 'application/msword', 
                       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                       'text/csv'];
    if (!validTypes.includes(file.type)) {
      setErrors({ file: 'Invalid file type. Please upload PDF, Excel, Word, or CSV file.' });
      return;
    }

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      setErrors({ file: 'File size exceeds 25MB limit.' });
      return;
    }

    setErrors({});
    setBoqFile(file);

    // Upload file
    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'boq');

      const response = await api.post('/uploads/boq', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      if (response.data.success) {
        setBoqFile({
          ...file,
          url: response.data.data.url,
          uploaded: true
        });
      } else {
        setErrors({ file: response.data.error || 'Upload failed' });
        setBoqFile(null);
      }
    } catch (error) {
      console.error('File upload error:', error);
      setErrors({ file: error.response?.data?.error || 'Failed to upload file' });
      setBoqFile(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleNext = () => {
    const newErrors = {};

    if (!boqFile || !boqFile.url) {
      newErrors.file = 'Please upload a BOQ file';
    }

    if (selectedCategories.length === 0) {
      newErrors.categories = 'Please select at least one material category';
    }

    if (deliveryTimeframe === 'specific' && !deliveryDate) {
      newErrors.deliveryDate = 'Please select a delivery date';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formData = {
      requestType: 'boq_upload',
      boqFileUrl: boqFile.url,
      boqFileName: boqFile.name,
      boqFileType: boqFile.type,
      quoteEverything,
      materialCategories: selectedCategories,
      deliveryAddress: project?.location ? {
        street: '',
        suburb: '',
        city: project.location.split(',')[0] || '',
        province: '',
        postal_code: ''
      } : {},
      deliveryTimeframe,
      deliveryDate: deliveryTimeframe === 'specific' ? deliveryDate : null,
      specialRequirements,
      additionalNotes: specialRequirements
    };

    onNext(formData);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Upload Bill of Quantities
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBack && (
            <Button size="small" onClick={onBack} startIcon={<ArrowBack />}>
              Back
            </Button>
          )}
          {onCancel && (
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {project && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Project: <strong>{project.title}</strong>
        </Typography>
      )}

      {/* File Upload Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Upload Your BOQ
        </Typography>

        {!boqFile ? (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: errors.file ? 'error.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: errors.file ? 'error.light' : 'grey.50',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light'
              }
            }}
            onClick={() => document.getElementById('boq-file-input').click()}
          >
            <UploadFile sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              Drag & drop your BOQ file here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Accepted formats: PDF, Excel (.xlsx, .xls), Word (.docx), CSV
              <br />
              Maximum file size: 25MB
            </Typography>
            <input
              id="boq-file-input"
              type="file"
              hidden
              accept=".pdf,.xlsx,.xls,.doc,.docx,.csv"
              onChange={handleFileSelect}
            />
          </Box>
        ) : (
          <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <UploadFile sx={{ color: 'success.main' }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {boqFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(boqFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            </Box>
            <Button
              size="small"
              color="error"
              startIcon={<Delete />}
              onClick={() => {
                setBoqFile(null);
                setErrors({});
              }}
            >
              Remove
            </Button>
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}

        {errors.file && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrors({ ...errors, file: null })}>
            {errors.file}
          </Alert>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => alert('Template download coming soon')}>
            Use Our Template
          </Button>
          <Button variant="outlined" size="small" onClick={() => alert('Sample view coming soon')}>
            View Sample
          </Button>
        </Box>
      </Paper>

      {/* Quote Everything or Specific Items */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            What do you need quoted? *
          </FormLabel>
          <RadioGroup
            value={quoteEverything}
            onChange={(e) => setQuoteEverything(e.target.value === 'true')}
          >
            <FormControlLabel
              value={true}
              control={<Radio />}
              label="I need quotes for EVERYTHING in the BOQ (Suppliers will quote all items listed)"
            />
            <FormControlLabel
              value={false}
              control={<Radio />}
              label="I only need SPECIFIC ITEMS from the BOQ (Specify which items below)"
            />
          </RadioGroup>
        </FormControl>
      </Paper>

      {/* Material Categories */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Material Categories Needed (Check all that apply) *
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This helps us send your request to the right suppliers:
        </Typography>

        <Grid container spacing={1}>
          {MATERIAL_CATEGORIES.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category}>
              <Chip
                label={category}
                onClick={() => handleCategoryToggle(category)}
                color={selectedCategories.includes(category) ? 'primary' : 'default'}
                variant={selectedCategories.includes(category) ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            </Grid>
          ))}
        </Grid>

        {errors.categories && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.categories}
          </Alert>
        )}
      </Paper>

      {/* Additional Notes */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Additional Notes or Special Requirements (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="E.g., 'Need delivery by Jan 15th', 'Prefer PPC cement', 'Looking for eco-friendly options', etc."
          value={specialRequirements}
          onChange={(e) => setSpecialRequirements(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={`${specialRequirements.length}/500 characters`}
        />
      </Paper>

      {/* Delivery Location */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Delivery Location
        </Typography>
        <TextField
          fullWidth
          value={project?.location || ''}
          disabled
          helperText="Auto-filled from project address"
          sx={{ mb: 2 }}
        />
        <Button size="small" variant="outlined">
          Edit
        </Button>
      </Paper>

      {/* Delivery Timeframe */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            When do you need materials delivered? *
          </FormLabel>
          <RadioGroup
            value={deliveryTimeframe}
            onChange={(e) => setDeliveryTimeframe(e.target.value)}
          >
            {DELIVERY_TIMEFRAMES.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>

          {deliveryTimeframe === 'specific' && (
            <TextField
              type="date"
              fullWidth
              label="Delivery Date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
              error={!!errors.deliveryDate}
              helperText={errors.deliveryDate}
            />
          )}
        </FormControl>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={onCancel || onBack} variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={<ArrowForward />}
          disabled={uploading}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          Continue to Select Suppliers
        </Button>
      </Box>
    </Box>
  );
};

export default BOQUploadStep;

